import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { AlertTriangle, TrendingDown, Zap, Link2 } from 'lucide-react';
import { t } from '@/i18n';
import { getSlotDetails, getFraction, getPackagingLine } from '@/data/mockData';

// Krátké jednotné zkratky materiálů pro konzistentní vzhled
const materialShort = {
  'Limestone GCC': 'GCC',
  'Limestone PCC': 'PCC',
  'By-product': 'Vedl.',
};

// OTE pásma — pozadí slotu a badge.
const tariffStyles = {
  negative: {
    bg: 'bg-blue-50/70',
    border: 'border-l-4 border-l-blue-500',
    badge: 'text-blue-700 border-blue-200 bg-blue-50',
    label: t('planning.tariffNegative'),
    icon: TrendingDown,
  },
  offpeak: {
    bg: 'bg-emerald-50/70',
    border: 'border-l-4 border-l-emerald-500',
    badge: 'text-emerald-700 border-emerald-200 bg-emerald-50',
    label: t('planning.off'),
    icon: null,
  },
  standard: {
    bg: 'bg-amber-50/40',
    border: 'border-l-4 border-l-amber-400',
    badge: 'text-amber-700 border-amber-200 bg-amber-50',
    label: t('planning.tariffStandard'),
    icon: null,
  },
  peak: {
    bg: 'bg-rose-50/70',
    border: 'border-l-4 border-l-rose-500',
    badge: 'text-rose-700 border-rose-200 bg-rose-50',
    label: t('planning.peak'),
    icon: Zap,
  },
};

export const PlanningCell = ({ slot, lineId, materialColor, onClick, oteOverlay = false }) => {
  if (!slot || !slot.material) {
    return (
      <Card
        className="p-4 min-h-[120px] flex items-center justify-center cursor-pointer border-2 border-dashed border-border bg-muted/40 hover:border-primary/40 hover:bg-primary/5 transition-all duration-200"
        onClick={onClick}
      >
        <span className="text-sm text-muted-foreground/70">{t('planning.schedule')}</span>
      </Card>
    );
  }

  const shortMaterial = materialShort[slot.material] || slot.material.split(' ')[0];
  const tariff = slot.energyTariff && tariffStyles[slot.energyTariff] ? tariffStyles[slot.energyTariff] : null;
  const TariffIcon = tariff?.icon;

  const details = lineId ? getSlotDetails(lineId, slot.day) : null;
  const fraction = details ? getFraction(details.fractionId) : null;
  const packagingLine = details ? getPackagingLine(details.packagingLineId) : null;
  const hasLinkedDeps = details && details.linkedTo && details.linkedTo.length > 0;

  return (
    <Card
      className={cn(
        'min-h-[120px] cursor-pointer hover:shadow-md hover:border-primary/40 transition-all duration-200 relative overflow-hidden border',
        oteOverlay && tariff ? `${tariff.bg} ${tariff.border}` : 'bg-white border-border/80',
      )}
      onClick={onClick}
    >
      {/* Material color bar – jediný barevný akcent (pokud OTE overlay vypnutý) */}
      {!oteOverlay && materialColor && (
        <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: materialColor }} />
      )}
      {/* Top dot indikátor materiálu (pokud OTE overlay zapnutý) */}
      {oteOverlay && materialColor && (
        <div className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full" style={{ backgroundColor: materialColor }} title={slot.material} />
      )}

      {/* Warning icon for high utilization */}
      {slot.utilization > 90 && !oteOverlay && (
        <div className="absolute top-2 right-2 z-10">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
        </div>
      )}

      <div className={cn('p-3 space-y-2', oteOverlay ? 'pt-3' : 'pt-4')}>
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-1.5">
            <div className="text-xs font-semibold text-foreground" title={slot.material}>{shortMaterial}</div>
            {hasLinkedDeps && (
              <Link2
                className="h-3 w-3 text-violet-600"
                title={`${t('planning.linkedTo')}: ${details.linkedTo.join(', ')}`}
              />
            )}
          </div>
          {oteOverlay && TariffIcon && <TariffIcon className="h-3 w-3 opacity-60" />}
        </div>
        {slot.orderId && (
          <div className="text-[10px] text-muted-foreground font-mono" title={slot.orderId}>{slot.orderId}</div>
        )}
        <Progress value={slot.utilization} className="h-2" />
        <div className="flex items-center justify-between gap-1 text-[11px]">
          <span className="text-muted-foreground tabular-nums">{slot.utilization}%</span>
          {tariff && (
            <Badge
              variant="outline"
              className={cn('text-[10px] px-1.5 py-0 border font-medium', tariff.badge)}
            >
              {tariff.label}
            </Badge>
          )}
        </div>
        {(fraction || packagingLine) && (
          <div className="flex items-center gap-1 pt-1 border-t border-border/40">
            {fraction && (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded border"
                style={{ backgroundColor: `${fraction.color}1a`, borderColor: fraction.color }}
                title={`${t('planning.fraction')} · ${t('planning.fractionCycleNote')} ×${fraction.cycleMultiplier.toFixed(1)}`}
              >
                {t(fraction.labelKey)}
              </span>
            )}
            {packagingLine && (
              <span
                className="inline-flex items-center text-[9px] text-muted-foreground/80 truncate"
                title={`${t('planning.packagingLine')}: ${t(packagingLine.labelKey)}`}
              >
                · {t(packagingLine.labelKey)}
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
